import defaultAttributes from "backend/defaultAttributes";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import useTopElements from "backend/hooks/model/useTopElements";
import { ICardElementWithChildren } from "backend/model/model";
import CascadeToolbar from "components/toolbars/CascadeToolbar";
import { DivisionCard } from "design/components/cards/DivisionCard";
import MeasureGridArea from "design/components/grids/MeasureGridArea";
import PageContainer from "design/layouts/PageContainer";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import Query from "qpr-base/querybuilder/Query";
import { getFileApiURL } from "qpr-base/utils/url-utils";
import React from "react";
import { useQuery } from "react-query";

const CascadePage = () => {
  const ctx = useAppContext();
  const link = useLink();
  const { data: topElems, isLoading: topElementsLoading } = useTopElements();
  const rootSCId = topElems?.find(
    (el) => el.scorecardsymbol === "L0"
  )?.scorecardid;

  const { data: periodlevels } = usePeriodLevels();
  const {
    data,
    isLoading: isDataLoading,
    isError,
    isPreviousData,
    isRefetching,
  } = useHierarchy(rootSCId);

  const { data: topElements, isLoading: isLoadingTopElements } = useQuery(
    `topelements-cascade-${ctx.period}`,
    () => {
      return Query(data?.map((el) => el.id))
        .topelement()
        .attributes(...defaultAttributes(ctx.period, periodlevels!))
        .fetchValues<ICardElementWithChildren>()
        .then((list) =>
          list.sort((a, b) => {
            if (!a.symbol || !b.symbol) return 0;
            return a.symbol.localeCompare(b.symbol);
          })
        );
    },
    { enabled: !!data && !!periodlevels }
  );

  return (
    <Page
      isLoading={
        !topElements ||
        !topElems ||
        !data ||
        topElementsLoading ||
        isLoadingTopElements ||
        isDataLoading ||
        isRefetching ||
        isPreviousData
      }
      Toolbar={CascadeToolbar}
    >
      <PageContainer width="1000px">
        <MeasureGridArea>
          {(data || [])
            .sort((a, b) => {
              if (!a.symbol || !b.symbol) return 0;
              return a.symbol.localeCompare(b.symbol);
            })
            .map((sc) => {
              const topE = topElems?.find((te) => te.scorecardid === sc.id);
              const top = topElements?.find((t) => {
                return t.id === topE?.id;
              });
              if (!top) {
                return null;
              }
              return (
                <DivisionCard
                  key={sc.id}
                  translatedName={getTranslatedAttribute(
                    topElems?.find((te) => te.id === top.id),
                    "Name",
                    ctx.language
                  )}
                  topElement={top}
                  rangeColor={top.rangecolor}
                  rangeName={top.rangename}
                  cardTitle={t(`cascade.Division`)}
                  departmentIcon={getFileApiURL(`/icons/divisions/DEP_1.svg`)}
                  backgroundImage={getFileApiURL(
                    `/images/divisions/divisions.png`
                  )}
                  typenames={[
                    {
                      name: "Scorecard",
                      title: t("cascade.Departments"),
                    },
                  ]}
                  scorecard={sc}
                  onSelect={() => {
                    if (sc.symbol)
                      link({ page: "strategymap", meaId: sc.topelement });
                  }}
                  onSelectChild={(item) => {
                    if (item.symbol)
                      link({ page: "strategymap", meaId: item.topelement });
                  }}
                />
              );
            })}
        </MeasureGridArea>
      </PageContainer>
    </Page>
  );
};
export default CascadePage;
